import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import adminTicketService from "../../services/adminTicketService";
import { validateFileByMimeAndSignature } from "../../services/fileValidation";
import {
  applyPollResult,
  buildDescriptionMessage,
  buildSentChatMessage,
  canCloseTicket,
  canReopenTicket,
  canSendChatMessage,
  collectOwnUnreadMessageIds,
  getChatDisabledTooltip,
  getPollAfterTimestamp,
  mergeChatMessages,
  sortChatMessagesAscending,
} from "../../services/adminTicketUtils";
import {
  downloadBase64File,
  getApiErrorMessage,
  getLoadMoreLabel,
  resolveEditedAttachmentName,
  resolvePendingAttachmentLabel,
} from "./adminTicketChatHelpers";

const TICKET_MESSAGE_POLL_INTERVAL_SEC = 30;
const ALLOWED_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];

export function useAdminTicketChat({
  ticket,
  loginId,
  isAdmin,
  onTicketUpdated,
  onMessagesRead,
  refreshSummary,
  showError,
  showSuccess,
}) {
  const threadRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesRef = useRef([]);
  const onMessagesReadRef = useRef(onMessagesRead);
  const showErrorRef = useRef(showError);
  const scrollToBottomRef = useRef(false);

  useEffect(() => {
    onMessagesReadRef.current = onMessagesRead;
    showErrorRef.current = showError;
  });

  const [messages, setMessages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingAttachmentName, setEditingAttachmentName] = useState(null);
  const [attachmentRemoved, setAttachmentRemoved] = useState(false);

  messagesRef.current = messages;

  const chatWritable = canSendChatMessage(ticket, loginId);
  const readOnly = !chatWritable;
  const isEditing = Boolean(editingId);
  const hasExistingAttachment = Boolean(editingAttachmentName && !attachmentRemoved);
  const hasPendingAttachment = Boolean(file) || hasExistingAttachment;
  const canSubmit = Boolean(messageText.trim()) || hasPendingAttachment;

  const resetComposer = useCallback(() => {
    setEditingId(null);
    setEditingAttachmentName(null);
    setAttachmentRemoved(false);
    setMessageText("");
    setFile(null);
    setAttachmentError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const descriptionMessage = useMemo(() => buildDescriptionMessage(ticket), [ticket]);
  const visibleMessages = useMemo(
    () => sortChatMessagesAscending(messages).filter((msg) => msg.id !== editingId),
    [messages, editingId]
  );
  const hasChatMessages = visibleMessages.length > 0;
  const showPaginationChip = totalCount > 10;
  const canLoadMore = messages.length < totalCount;
  const loadMoreLabel = getLoadMoreLabel(loadingMore, canLoadMore);

  const loadPage = useCallback(async (pageToLoad, { append = false, preserveScroll = false } = {}) => {
    const setLoader = append ? setLoadingMore : setLoading;
    const threadEl = preserveScroll ? threadRef.current : null;
    const prevScrollHeight = threadEl?.scrollHeight ?? 0;
    const prevScrollTop = threadEl?.scrollTop ?? 0;

    setLoader(true);
    try {
      const res = await adminTicketService.listMessages({ ticketId: ticket.id, page: pageToLoad });
      setTotalCount(res.totalCount);
      setPage(res.page);
      setMessages((prev) => (append ? mergeChatMessages(prev, res.items) : res.items));
      if (!append && pageToLoad === 0) {
        scrollToBottomRef.current = true;
        onMessagesReadRef.current?.(ticket.id);
      } else if (append && threadEl) {
        requestAnimationFrame(() => {
          const el = threadRef.current;
          if (!el) return;
          el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight);
        });
      }
    } catch (e) {
      showErrorRef.current(getApiErrorMessage(e, "Errore caricamento messaggi"));
    } finally {
      setLoader(false);
    }
  }, [ticket.id]);

  const pollNewMessages = useCallback(async () => {
    try {
      const currentMessages = messagesRef.current;
      const res = await adminTicketService.pollMessages({
        ticketId: ticket.id,
        afterTimestamp: getPollAfterTimestamp(currentMessages),
        ownMessageIds: collectOwnUnreadMessageIds(currentMessages, (msg) => msg.operator),
      });
      const hadNewItems = Boolean(res.items?.length);
      const hadReadUpdates = Boolean(res.readMessageIds?.length);
      if (!hadNewItems && !hadReadUpdates) return;

      setMessages((prev) => {
        const next = applyPollResult(prev, res);
        const added = next.length - prev.length;
        if (added > 0) {
          setTotalCount((count) => count + added);
          scrollToBottomRef.current = true;
        }
        return next;
      });
      if (hadNewItems || hadReadUpdates) {
        onMessagesReadRef.current?.(ticket.id);
        refreshSummary?.();
      }
    } catch (e) {
      console.warn("admin ticket message poll failed", e);
    }
  }, [refreshSummary, ticket.id]);

  useEffect(() => {
    setMessages([]);
    setPage(0);
    setTotalCount(0);
    resetComposer();
    loadPage(0, { append: false });
  }, [ticket.id, loadPage, resetComposer]);

  useEffect(() => {
    if (loading) return undefined;
    pollNewMessages();
    const ms = Math.max(1, TICKET_MESSAGE_POLL_INTERVAL_SEC) * 1000;
    const timer = setInterval(pollNewMessages, ms);
    return () => clearInterval(timer);
  }, [ticket.id, pollNewMessages, loading]);

  useEffect(() => {
    if (loading || !scrollToBottomRef.current) return;
    scrollToBottomRef.current = false;
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [visibleMessages, loading, editingId]);

  const handleFileChange = useCallback(async (event) => {
    if (readOnly) return;
    const selected = event.target.files?.[0];
    if (!selected) return;
    event.target.value = "";
    if (selected.size > 2 * 1024 * 1024) {
      setFile(null);
      setAttachmentError("Allegato non valido (max 2MB, consentiti: JPEG/PNG/PDF)");
      return;
    }
    const mimeValidation = await validateFileByMimeAndSignature(selected, ALLOWED_ATTACHMENT_TYPES);
    if (!mimeValidation.ok) {
      setFile(null);
      setAttachmentError(mimeValidation.message);
      return;
    }
    setAttachmentError("");
    setFile(selected);
    setAttachmentRemoved(false);
  }, [readOnly]);

  const handleRemoveAttachment = useCallback(() => {
    setFile(null);
    setAttachmentError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (editingAttachmentName) {
      setAttachmentRemoved(true);
    }
  }, [editingAttachmentName]);

  const handleSend = useCallback(async () => {
    if (!chatWritable || !canSubmit) return;
    const trimmed = messageText.trim();
    setSending(true);
    try {
      if (isEditing) {
        await adminTicketService.editMessage({
          ticketId: ticket.id,
          messageId: editingId,
          action: "edit",
          message: trimmed || undefined,
          file,
          removeAttachment: Boolean(editingAttachmentName && attachmentRemoved && !file),
          existingAttachmentKept: hasExistingAttachment,
        });
        const nextAttachmentName = resolveEditedAttachmentName({
          file,
          attachmentRemoved,
          editingAttachmentName,
        });
        setMessages((prev) => prev.map((msg) => (
          msg.id === editingId
            ? { ...msg, message: trimmed || null, attachmentName: nextAttachmentName }
            : msg
        )));
        resetComposer();
        return;
      }

      const res = await adminTicketService.sendMessage({
        ticketId: ticket.id,
        message: trimmed || undefined,
        file,
      });
      const sentMessage = buildSentChatMessage({
        messageId: res.messageId,
        message: trimmed,
        attachmentName: file?.name ?? null,
        isOperatorSide: true,
      });
      setMessages((prev) => mergeChatMessages(prev, [sentMessage]));
      setTotalCount((prev) => prev + 1);
      scrollToBottomRef.current = true;
      resetComposer();
    } catch (e) {
      showError(getApiErrorMessage(e, "Errore invio messaggio"));
    } finally {
      setSending(false);
    }
  }, [
    attachmentRemoved,
    canSubmit,
    chatWritable,
    editingAttachmentName,
    editingId,
    file,
    hasExistingAttachment,
    isEditing,
    messageText,
    resetComposer,
    showError,
    ticket.id,
  ]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
    if (event.key === "Escape" && isEditing) {
      event.preventDefault();
      resetComposer();
    }
  }, [handleSend, isEditing, resetComposer]);

  const handleDownload = useCallback(async (messageId) => {
    try {
      const res = await adminTicketService.downloadAttachment({ ticketId: ticket.id, messageId });
      downloadBase64File(res.attachmentBase64, res.attachmentName);
    } catch (e) {
      showError(getApiErrorMessage(e, "Errore download allegato"));
    }
  }, [showError, ticket.id]);

  const handleDelete = useCallback(async (messageId) => {
    if (editingId === messageId) {
      resetComposer();
    }
    try {
      await adminTicketService.editMessage({
        ticketId: ticket.id,
        messageId,
        action: "delete",
      });
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      setTotalCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      showError(getApiErrorMessage(e, "Errore eliminazione messaggio"));
    }
  }, [editingId, resetComposer, showError, ticket.id]);

  const handleEditStart = useCallback((msg) => {
    setEditingId(msg.id);
    setMessageText(msg.message ?? "");
    setEditingAttachmentName(msg.attachmentName ?? null);
    setAttachmentRemoved(false);
    setFile(null);
    setAttachmentError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleClose = useCallback(async () => {
    try {
      await adminTicketService.closeTicket({ ticketId: ticket.id });
      showSuccess("Ticket chiuso");
      onTicketUpdated?.();
    } catch (e) {
      showError(getApiErrorMessage(e, "Errore chiusura ticket"));
    }
  }, [onTicketUpdated, showError, showSuccess, ticket.id]);

  const handleReopen = useCallback(async () => {
    try {
      await adminTicketService.reopenTicket({ ticketId: ticket.id });
      showSuccess("Ticket riaperto");
      onTicketUpdated?.();
    } catch (e) {
      showError(getApiErrorMessage(e, "Errore riapertura ticket"));
    }
  }, [onTicketUpdated, showError, showSuccess, ticket.id]);

  const handleLoadMore = useCallback(() => {
    loadPage(page + 1, { append: true, preserveScroll: true });
  }, [loadPage, page]);

  return {
    threadRef,
    fileInputRef,
    loading,
    loadingMore,
    messageText,
    setMessageText,
    sending,
    attachmentError,
    isEditing,
    readOnly,
    canSubmit,
    disabledTooltip: getChatDisabledTooltip(ticket, loginId),
    descriptionMessage,
    visibleMessages,
    hasChatMessages,
    showPaginationChip,
    canLoadMore,
    loadMoreLabel,
    page,
    showClose: canCloseTicket(ticket, loginId, isAdmin),
    showReopen: canReopenTicket(ticket, loginId, isAdmin),
    showAttachmentRemove: Boolean(file || hasExistingAttachment),
    pendingAttachmentLabel: resolvePendingAttachmentLabel({
      file,
      hasExistingAttachment,
      editingAttachmentName,
    }),
    chatWritable,
    resetComposer,
    handleFileChange,
    handleRemoveAttachment,
    handleSend,
    handleKeyDown,
    handleDownload,
    handleDelete,
    handleEditStart,
    handleClose,
    handleReopen,
    handleLoadMore,
  };
}
 